import logger from "../utils/logger";
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from '../slices/userReducer';
import {
  trackButtonClickAnalytics,
  isClickableElement,
  collectClickMetadata
} from '../utils/visitorTracking';

/**
 * Custom hook for automatic button click tracking
 * Uses global event delegation to capture all button interactions
 */
export const useButtonTracking = (options = {}) => {
  const user = useSelector(selectUser);
  const isInitialized = useRef(false);
  const clickQueue = useRef([]);
  const isProcessing = useRef(false);

  const {
    enabled = true,
    debounceMs = 100,
    batchSize = 5,
    excludeClasses = ['no-track', 'ignore-click'],
    excludeIds = [],
    debug = false
  } = options;

  // Batch processing for better performance
  const processBatch = async () => {
    if (isProcessing.current || clickQueue.current.length === 0) return;

    isProcessing.current = true;
    const batch = clickQueue.current.splice(0, batchSize);

    try {
      // Process all clicks in the batch
      await Promise.all(
        batch.map(clickData =>
          trackButtonClickAnalytics(clickData, user?.id || null)
        )
      );

      if (debug) {
      }
    } catch (error) {
      // Fail silently — tracking should never break the app
    }

    isProcessing.current = false;

    // Process remaining items if any
    if (clickQueue.current.length > 0) {
      setTimeout(processBatch, debounceMs);
    }
  };

  // Debounced batch processor
  const debouncedProcess = useRef(
    (() => {
      let timeout;
      return () => {
        clearTimeout(timeout);
        timeout = setTimeout(processBatch, debounceMs);
      };
    })()
  );

  // Main click handler
  const handleGlobalClick = (event) => {
    if (!enabled) return;

    const target = event.target;
    if (!target) return;

    // Check if element should be excluded
    const hasExcludedClass = excludeClasses.some(cls =>
      target.classList && target.classList.contains(cls)
    );
    const hasExcludedId = excludeIds.includes(target.id);

    if (hasExcludedClass || hasExcludedId) {
      if (debug) 
      return;
    }

    // Find the actual clickable element (might be a parent)
    let clickableElement = target;
    let depth = 0;
    const maxDepth = 5;

    while (clickableElement && depth < maxDepth) {
      if (isClickableElement(clickableElement)) {
        break;
      }
      clickableElement = clickableElement.parentElement;
      depth++;
    }

    // If we found a clickable element, track it
    if (clickableElement && isClickableElement(clickableElement)) {
      try {
        const metadata = collectClickMetadata(event, clickableElement);

        if (debug) {
        }

        // Add to queue for batch processing
        clickQueue.current.push(metadata);
        debouncedProcess.current();

      } catch (error) {
        logger.error('Error collecting click metadata:', error);
      }
    }
  };

  // Initialize global click listener
  useEffect(() => {
    if (!enabled || isInitialized.current) return;

    // Add global click listener with capture=true to catch all clicks
    document.addEventListener('click', handleGlobalClick, {
      capture: true,
      passive: true
    });

    isInitialized.current = true;

    if (debug) {
    }

    // Cleanup function
    return () => {
      document.removeEventListener('click', handleGlobalClick, { capture: true });
      isInitialized.current = false;

      if (debug) {
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, debug]);

  // Process any remaining clicks when user changes
  useEffect(() => {
    if (clickQueue.current.length > 0) {
      debouncedProcess.current();
    }
  }, [user?.id]);

  // Expose methods for manual tracking if needed
  return {
    trackClick: (element, customEvent) => {
      if (!element) return;

      const syntheticEvent = customEvent || {
        target: element,
        clientX: 0,
        clientY: 0,
        pageX: 0,
        pageY: 0
      };

      handleGlobalClick(syntheticEvent);
    },

    getQueueSize: () => clickQueue.current.length,

    flushQueue: () => {
      if (clickQueue.current.length > 0) {
        processBatch();
      }
    }
  };
};

export default useButtonTracking;